<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import { LockClosedIcon, ArrowUpIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  open: { type: Boolean, default: false },
  feature: { type: String, default: '' },
  resource: { type: String, default: '' },
  currentUsage: { type: Number, default: 0 },
  limit: { type: Number, default: 0 },
  requiredPlan: { type: String, default: 'starter' },
});

const emit = defineEmits(['close']);

const router = useRouter();

const title = computed(() => {
  if (props.resource) {
    const labels = {
      events_created: 'Events',
      emails_sent: 'Emails',
      sms_sent: 'SMS Messages',
      evaluation_ocr: 'Evaluation OCR',
      attendees: 'Attendee Records',
      seats: 'Team Members',
      offices: 'Offices',
      campaigns: 'Campaigns',
    };
    return `${labels[props.resource] || props.resource} Limit Reached`;
  }
  const labels = {
    waitlist_auto_promote: 'Automatic Waitlist Promotion',
    advanced_reporting: 'Advanced Reporting',
    ai_reports: 'AI-Powered Reports',
    evaluation_ocr: 'Evaluation OCR Processing',
    planning_periods: 'Event Planning Periods',
    custom_templates: 'Custom Message Templates',
    appointment_booking: 'Appointment Booking',
    white_label: 'White-Label Branding',
    lead_management_filters: 'Lead Management Filters',
    custom_email_domain: 'Custom Email Sending Domain',
  };
  return labels[props.feature] || 'Premium Feature';
});

const description = computed(() => {
  if (props.resource) {
    return `You've used ${props.currentUsage} of ${props.limit} on your current plan. Upgrade to get more.`;
  }
  return `This feature is available on the ${planLabel.value} plan and above. Upgrade to unlock it.`;
});

const planLabel = computed(() => {
  const labels = { starter: 'Starter', professional: 'Professional', enterprise: 'Enterprise' };
  return labels[props.requiredPlan] || 'Professional';
});

function handleUpgrade() {
  emit('close');
  router.push(`/upgrade/${props.requiredPlan}`);
}
</script>

<template>
  <TransitionRoot appear :show="open" as="template">
    <Dialog as="div" class="relative z-50" @close="emit('close')">
      <TransitionChild
        enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            enter="ease-out duration-300" enter-from="opacity-0 scale-95" enter-to="opacity-100 scale-100"
            leave="ease-in duration-200" leave-from="opacity-100 scale-100" leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-8 text-left shadow-2xl transition-all">
              <div class="flex justify-center mb-4">
                <div class="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                  <LockClosedIcon v-if="feature" class="w-7 h-7 text-action-600" />
                  <ArrowUpIcon v-else class="w-7 h-7 text-action-600" />
                </div>
              </div>

              <DialogTitle as="h3" class="text-xl font-bold text-gray-900 text-center">
                {{ title }}
              </DialogTitle>

              <p class="mt-3 text-gray-500 text-center text-sm leading-relaxed">
                {{ description }}
              </p>

              <div class="mt-6 flex flex-col gap-3">
                <button
                  @click="handleUpgrade"
                  class="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-200"
                >
                  Upgrade to {{ planLabel }}
                </button>
                <button
                  @click="emit('close')"
                  class="w-full py-3 px-4 text-gray-500 font-medium hover:text-gray-700 transition-colors text-sm"
                >
                  Maybe Later
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
